import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button.js';
import { ProviderIcon } from '../../../shared/ui/ProviderIcon.js';
import {
  CUSTOMER_BANK_TRANSFER_PROVIDER_ID,
  CUSTOMER_GATEWAY_PROVIDER_ID,
  CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH,
} from '../paymentProvidersApi.js';
import type { CustomerPaymentMethodId } from '../../shop/presenters/shopCheckoutPresenter.js';

export interface PaymentMethodPickerOption {
  readonly id: CustomerPaymentMethodId;
  readonly testId: string;
}

export interface PaymentMethodPickerProps {
  readonly options: readonly PaymentMethodPickerOption[];
  readonly selectedId: CustomerPaymentMethodId | null;
  readonly onSelect: (id: CustomerPaymentMethodId) => void;
  readonly amountMinor: number;
  readonly currency: string;
  readonly ctaLabel: string;
  readonly ctaDisabled: boolean;
  readonly ctaPending: boolean;
  readonly onConfirm: () => void;
}

function formatCheckoutAmount(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  const formatted = Number.isInteger(major) ? major.toString() : major.toFixed(2);
  return `${formatted} ${currency}`;
}

function CheckmarkIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true">
      <path
        d="M3.5 8.2 6.4 11 12.5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PaymentMethodOptionCard({
  selected,
  disabled,
  onSelect,
  title,
  description,
  icon,
  testId,
  name,
  value,
}: {
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
  readonly title: string;
  readonly description: string;
  readonly icon: ReactNode;
  readonly testId: string;
  readonly name: string;
  readonly value: string;
}): JSX.Element {
  return (
    <label
      data-testid={testId}
      className={[
        'flex cursor-pointer items-center gap-3 rounded-2xl border-2 bg-[var(--color-surface)] px-4 py-3.5 transition-colors',
        selected ? 'border-[var(--color-accent)]' : 'border-[var(--color-border)]',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-[var(--color-accent)]/45',
      ].join(' ')}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
        className="sr-only"
      />
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold text-[var(--color-on-surface)]">{title}</span>
        <span className="mt-0.5 block text-sm text-[var(--color-on-surface-muted)]">
          {description}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={[
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
          selected
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-foreground,#fff)]'
            : 'border-[var(--color-border)] bg-transparent',
        ].join(' ')}
      >
        {selected ? <CheckmarkIcon /> : null}
      </span>
    </label>
  );
}

function paymentMethodIcon(methodId: CustomerPaymentMethodId): ReactNode {
  const iconShell =
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-surface-muted)]';
  if (methodId === 'QR') {
    return (
      <span className={iconShell}>
        <ProviderIcon
          providerId={CUSTOMER_BANK_TRANSFER_PROVIDER_ID}
          size="sm"
          assetBasePath={CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH}
          title=""
        />
      </span>
    );
  }
  if (methodId === 'CARD') {
    return (
      <span className={iconShell}>
        <ProviderIcon
          providerId={CUSTOMER_GATEWAY_PROVIDER_ID}
          size="sm"
          assetBasePath={CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH}
          title=""
        />
      </span>
    );
  }
  if (methodId === 'CASH') {
    return (
      <span className={iconShell} aria-hidden="true">
        <svg viewBox="0 0 24 24" className="h-6 w-6 text-[var(--color-on-surface-muted)]">
          <path
            d="M4 6h16v12H4V6Zm2 2v8h12V8H6Zm2 2h2v4H8v-4Zm4 0h4v4h-4v-4Z"
            fill="currentColor"
          />
        </svg>
      </span>
    );
  }
  return (
    <span className={iconShell} aria-hidden="true">
      <svg viewBox="0 0 24 24" className="h-6 w-6 text-[var(--color-on-surface-muted)]">
        <path
          d="M4 8h16v8H4V8Zm0-2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H4Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

export function PaymentMethodPicker({
  options,
  selectedId,
  onSelect,
  amountMinor,
  currency,
  ctaLabel,
  ctaDisabled,
  ctaPending,
  onConfirm,
}: PaymentMethodPickerProps): JSX.Element {
  const { t } = useTranslation('customer');
  const pickerDisabled = ctaDisabled || ctaPending;

  return (
    <div className="flex flex-col gap-4" data-testid="customer-payment-method-picker">
      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="sr-only">{t('checkout.selectMethodTitle')}</legend>
        {options.map((option) => (
          <PaymentMethodOptionCard
            key={option.id}
            name="customer-payment-method"
            value={option.id}
            testId={option.testId}
            selected={selectedId === option.id}
            disabled={pickerDisabled}
            title={t(`payment.methodOption.${option.id}.title`)}
            description={t(`payment.methodOption.${option.id}.description`)}
            icon={paymentMethodIcon(option.id)}
            onSelect={() => {
              onSelect(option.id);
            }}
          />
        ))}
      </fieldset>

      <div className="flex flex-col gap-3 border-t border-[var(--color-border)] pt-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-base font-medium text-[var(--color-on-surface)]">
            {t('payment.totalDue')}
          </span>
          <span className="text-xl font-bold text-[var(--color-on-surface)]">
            {formatCheckoutAmount(amountMinor, currency)}
          </span>
        </div>
        <Button
          intent="primary"
          size="lg"
          block
          type="button"
          data-testid="customer-payment-pay-cta"
          disabled={pickerDisabled || selectedId === null}
          onClick={onConfirm}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}
